/**
 * Cross-route stash for the "bounce to Login and auto-return" E2EE recovery
 * flow. When a student's in-memory E2EE identity is missing (login-time
 * provisioning failed) and they choose to repair it, Class can't re-provision
 * because it never has the password. So Class stashes the current room name
 * here and navigates to the Login route; after the student re-enters their
 * password and provisioning succeeds, Login reads the stashed room and
 * navigates back to /class to auto-rejoin.
 *
 * State lives in `sessionStorage` (NOT localStorage): it must survive the SPA
 * route change AND a full reload, yet auto-clear when the tab closes so a stale
 * room can't trigger a surprise rejoin in a future session. Every access is
 * guarded — sessionStorage can throw in private-mode browsers and is absent
 * under SSR — and any failure degrades to a graceful no-op / null.
 */

/** Single storage key for the pending return-room name. */
const STORAGE_KEY = "avatar-e2ee-return-room";

/**
 * Stash the room name to return to after a successful Login re-provision.
 * No-op for an empty/whitespace room or if storage is unavailable.
 */
export function setPendingReturnRoom(room: string): void {
  const trimmed = room.trim();
  if (!trimmed) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, trimmed);
  } catch {
    // Storage unavailable (private mode / SSR) — degrade silently.
  }
}

/**
 * Read AND clear the stashed room name (one-shot). Returns the trimmed room
 * name, or null if none is stored or storage is unavailable.
 */
export function takePendingReturnRoom(): string | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  } catch {
    return null;
  }
}

/** Peek whether a room is stashed, without clearing it. */
export function hasPendingReturnRoom(): boolean {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return !!value?.trim();
  } catch {
    return false;
  }
}

/** Explicitly clear any stashed return room. */
export function clearPendingReturnRoom(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}
